/**
 * The handoff card (slice 6c §3.2, §7.1; boards 90, 90b, 93): the agent asks
 * a person to take over the computer. It renders live from the thread's
 * `handoffs`, so it changes in place for everyone: waiting (Take over, Skip),
 * taken by me (Continue, Skip), taken by someone else (who and since when; no
 * Watch: watching would take their stream, §2), and ended, shrunk to one line.
 * The card's container decides its layout: the still beside the words from
 * 480 px, above the actions below it (a phone, or the side panel).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { threadKey, threadsKey } from "../chats/api";
import { shortTime } from "../chats/format";
import { fileUrl } from "../messaging/attached";
import { handoffAgain, handoffSkip, takeHandoff, type Handoff } from "./api";
import { ControlSheet } from "./control-sheet";
import type { FirstLink } from "./desktop-link";
import { endedLine, firstName, takeOn, whatToDo } from "./handoff-view";

const TakeIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden className="shrink-0">
    <path
      d="M9 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4M14 3h7v7M21 3l-9 9"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const LINE_ICON = {
  done: <path d="M20 6 9 17l-5-5" fill="none" stroke="var(--color-moss)" strokeWidth="2.2" />,
  skipped: (
    <path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="var(--color-hint)" strokeWidth="2" />
  ),
  expired: (
    <>
      <circle cx="12" cy="12" r="9" fill="none" stroke="var(--color-hint)" strokeWidth="2" />
      <path d="M12 7v5l3 2" fill="none" stroke="var(--color-hint)" strokeWidth="2" />
    </>
  ),
};

/** The screen still the agent took with its ask, else a drawn screen. */
function Still({ id }: { id: string | null }) {
  return (
    <div
      data-handoff-still
      className="aspect-video w-full shrink-0 overflow-clip rounded-lg bg-[#1B2232] @[480px]:h-[117px] @[480px]:w-[208px]"
    >
      {id ? (
        <img
          src={fileUrl(id, "thumb")}
          alt="The computer's screen when the agent asked"
          className="size-full object-cover object-top"
        />
      ) : (
        <div className="flex size-full flex-col gap-[5px] p-2">
          <div className="h-2 rounded-[3px] bg-[#2C3446]" />
          <div className="flex-1 rounded-[3px] bg-[#F4F5F7]" />
        </div>
      )}
    </div>
  );
}

function useInvalidate(threadId: string) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: threadKey(threadId) });
    void client.invalidateQueries({ queryKey: threadsKey });
  };
}

/** Board 93's one line, with Ask again on one nobody took (once). */
function EndedLine({ handoff, me }: { handoff: Handoff; me: Me }) {
  const refresh = useInvalidate(handoff.thread_id);
  const again = useMutation({ mutationFn: () => handoffAgain(handoff.id), onSuccess: refresh });
  const status = handoff.status as "done" | "skipped" | "expired";
  const { text, meta } = endedLine(handoff, me.workspace.timezone);
  const canAsk = status === "expired" && handoff.next_turn_id === null;
  return (
    <div className="flex w-full max-w-[652px] flex-col gap-1.5">
      <div
        data-handoff-card={status}
        className="flex min-h-11 w-full items-center gap-2.5 rounded-[10px] px-4 py-2.5 shadow-[inset_0_0_0_1px_var(--color-border)]"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden className="shrink-0">
          {LINE_ICON[status]}
        </svg>
        <span className="min-w-0 flex-1 text-sm leading-[18px] text-copy">{text}</span>
        {canAsk ? (
          <button
            type="button"
            onClick={() => again.mutate()}
            disabled={again.isPending}
            className="-my-2 -mr-2 h-11 shrink-0 rounded-md px-2 text-sm leading-[18px] font-medium text-foreground hover:bg-accent disabled:opacity-50"
          >
            Ask again
          </button>
        ) : (
          <span className="shrink-0 text-meta text-hint">{meta}</span>
        )}
      </div>
      {again.error ? (
        <p role="alert" className="text-meta text-brick">
          {errorHint(again.error)}
        </p>
      ) : null}
    </div>
  );
}

/** Board 93, "Someone has it": who and since when. No Watch (§2). */
function TakenByOther({ handoff, me }: { handoff: Handoff; me: Me }) {
  const who = handoff.taken_by ? firstName(handoff.taken_by.name) : "Someone";
  return (
    <div
      data-handoff-card="taken"
      className="flex w-full max-w-[652px] flex-col gap-3.5 rounded-panel bg-card px-5 py-[18px] shadow-[0_0_0_1px_var(--color-border)]"
    >
      <div className="text-xs leading-4 font-semibold tracking-[0.06em] text-moss uppercase">
        On the computer · {who} has it
      </div>
      <div className="text-lg leading-6 font-medium text-foreground">{handoff.reason}</div>
      {handoff.taken_at ? (
        <div className="text-meta text-muted-foreground">
          Since {shortTime(handoff.taken_at, me.workspace.timezone)}
        </div>
      ) : null}
    </div>
  );
}

/** The card's words and still: beside each other from 480 px, the still under the words below. */
function OpenBody({ handoff, showable }: { handoff: Handoff; showable: boolean }) {
  return (
    <div className="flex flex-col gap-3 @[480px]:flex-row @[480px]:items-start @[480px]:gap-[18px]">
      <div className="flex flex-col gap-1 @[480px]:order-2 @[480px]:gap-2 @[480px]:pt-0.5">
        <div className="text-lg leading-6 font-medium tracking-[-0.01em] text-foreground">
          {handoff.reason}
        </div>
        <p className="text-[15px] leading-[22px] text-copy @[480px]:text-sm @[480px]:leading-[21px]">
          {showable ? whatToDo(handoff) : "This computer's screen can't be shown in Crew."}
        </p>
        {handoff.host ? (
          <span className="hidden text-meta text-muted-foreground @[480px]:block">
            {handoff.host}
          </span>
        ) : null}
      </div>
      <div className="@[480px]:order-1">
        <Still id={handoff.still_file_id} />
      </div>
    </div>
  );
}

/** Take over (Continue for its taker; none where the screen can't be shown) and Skip. */
function OpenActions({
  take,
  skip,
  busy,
  note,
  elsewhere,
}: {
  take: (() => void) | null;
  skip: () => void;
  busy: boolean;
  /** "Anyone in the crew can do it", or "You have it · since 8:54". */
  note: { mine: boolean; text: string };
  /** Where Take over is done instead, when not here (S-455: a lean box, on a phone). */
  elsewhere: string | null;
}) {
  return (
    <>
      {elsewhere ? (
        <p className="text-meta leading-[19px] text-muted-foreground">{elsewhere}</p>
      ) : null}
      <div className="flex items-center gap-3 @[480px]:gap-4 @[480px]:border-t @[480px]:border-border @[480px]:pt-4">
        {take ? (
          <button
            type="button"
            onClick={take}
            disabled={busy}
            className={cn(
              "inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-[9px] bg-foreground text-[15px] leading-[18px] font-medium text-background hover:bg-foreground/90 disabled:opacity-60",
              "@[480px]:h-9 @[480px]:flex-none @[480px]:rounded-lg @[480px]:px-4 @[480px]:text-sm",
            )}
          >
            <TakeIcon size={14} />
            {note.mine ? "Continue" : "Take over"}
          </button>
        ) : null}
        <button
          type="button"
          onClick={skip}
          disabled={busy}
          className="inline-flex h-11 items-center rounded-md px-4 text-[15px] leading-[18px] font-medium text-foreground hover:bg-accent disabled:opacity-60 @[480px]:-ml-2 @[480px]:h-9 @[480px]:px-2 @[480px]:text-sm"
        >
          Skip
        </button>
        <span className="ml-auto hidden text-xs leading-4 text-hint @[480px]:block">
          {note.text}
        </span>
      </div>
    </>
  );
}

/** Take over (or Continue) opens the control sheet with the take's link; Skip ends it. */
function useOpenActions(handoff: Handoff) {
  const refresh = useInvalidate(handoff.thread_id);
  const [sheet, setSheet] = useState<{ first: FirstLink } | null>(null);
  const take = useMutation({
    mutationFn: () => takeHandoff(handoff.id),
    onSuccess: (r) => {
      setSheet({ first: r.desktop });
      refresh();
    },
    onError: refresh,
  });
  const skip = useMutation({ mutationFn: () => handoffSkip(handoff.id), onSuccess: refresh });
  return {
    sheet,
    closeSheet: () => setSheet(null),
    take: () => take.mutate(),
    skip: () => skip.mutate(),
    busy: take.isPending || skip.isPending,
    error: take.error ?? skip.error,
  };
}

/** What an open card shows: whose it is, who it's for, and the actions' note. */
function openView(handoff: Handoff, me: Me, mobile: boolean) {
  const mine = handoff.status === "taken" && handoff.taken_by?.id === me.user.id;
  const since = handoff.taken_at ? shortTime(handoff.taken_at, me.workspace.timezone) : "";
  return {
    mine,
    ...takeOn(me.computer, mobile),
    forName: handoff.for ? firstName(handoff.for.name) : null,
    agentName: me.agents.find((a) => a.id === handoff.agent_id)?.name ?? "Chief of Staff",
    note: { mine, text: mine ? `You have it · since ${since}` : "Anyone in the crew can do it" },
  };
}

/** Boards 90, 90b: the ask, with Take over (Continue for its taker) and Skip. */
function OpenCard({ handoff, me }: { handoff: Handoff; me: Me }) {
  const actions = useOpenActions(handoff);
  const { mine, showable, take, elsewhere, forName, agentName, note } = openView(
    handoff,
    me,
    useIsMobile(),
  );
  return (
    <div data-handoff-card={mine ? "mine" : "waiting"} className="@container w-full max-w-[652px]">
      <div className="flex flex-col gap-3 rounded-panel bg-card p-4 shadow-[0_0_0_1px_var(--color-border),0_1px_2px_#1F2A440A] @[480px]:gap-4 @[480px]:p-5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs leading-4 font-semibold tracking-[0.06em] text-copper">
            YOUR TURN · ON THE COMPUTER
          </span>
          {forName ? (
            <span className="hidden shrink-0 text-xs leading-4 text-hint @[480px]:block">
              for {forName}
            </span>
          ) : null}
        </div>
        <OpenBody handoff={handoff} showable={showable} />
        <OpenActions
          take={take ? actions.take : null}
          skip={actions.skip}
          busy={actions.busy}
          note={note}
          elsewhere={elsewhere}
        />
        {actions.error ? (
          <p role="alert" className="text-meta text-brick">
            {errorHint(actions.error)}
          </p>
        ) : null}
      </div>
      {actions.sheet ? (
        <ControlSheet
          open
          onClose={actions.closeSheet}
          handoff={handoff}
          first={actions.sheet.first}
          agentName={agentName}
        />
      ) : null}
    </div>
  );
}

/** A `handoff_card` message: live from the thread's `handoffs`, else its reason alone. */
export function HandoffCard({
  handoff,
  fallbackReason,
  me,
}: {
  handoff: Handoff | undefined;
  fallbackReason: string;
  me: Me;
}) {
  if (!handoff) {
    return (
      <div
        data-handoff-card="loading"
        className="flex w-full max-w-[652px] flex-col gap-3 rounded-panel bg-card p-5 shadow-[0_0_0_1px_var(--color-border)]"
      >
        <span className="text-xs leading-4 font-semibold tracking-[0.06em] text-copper">
          YOUR TURN · ON THE COMPUTER
        </span>
        <div className="text-lg leading-6 font-medium text-foreground">{fallbackReason}</div>
      </div>
    );
  }
  if (handoff.status === "waiting") return <OpenCard handoff={handoff} me={me} />;
  if (handoff.status === "taken") {
    return handoff.taken_by?.id === me.user.id ? (
      <OpenCard handoff={handoff} me={me} />
    ) : (
      <TakenByOther handoff={handoff} me={me} />
    );
  }
  return <EndedLine handoff={handoff} me={me} />;
}
